<script lang="ts">
  import {balances} from "$lib/stores";
  import type {IBalance, INetwork} from "$lib/interfaces/IBalance";
  import {onMount} from "svelte";
  import CopyIcon from "$lib/icons/CopyIcon.svelte";
  import {QRCode} from "@castlenine/svelte-qrcode";
  import CustomDropdown from "$lib/elements/CurrencyDropdown.svelte";
  import {toasts} from "$lib/stores/toastStores";
  import BonusOffers from "$lib/components/ModalContents/DepositWithdraw/Deposit/BonusOffers.svelte";

  let storedBalances: IBalance[] | null = $state(null);
  let selectedBalance: IBalance = $state();
  let selectedNetwork: INetwork = $state();
  let loading = $state(true);

  // Helper function to get USDT networks from USDTE and USDTT
  function getUSDTNetworks(balances: IBalance[]): INetwork[] {
    const usdte = balances.find(b => b.shortName === 'USDTE');
    const usdtt = balances.find(b => b.shortName === 'USDTT');
    const networks: INetwork[] = [];

    if (usdte?.cryptoNetworks[0]) {
      networks.push(usdte.cryptoNetworks[0]);
    }
    if (usdtt?.cryptoNetworks[0]) {
      networks.push(usdtt.cryptoNetworks[0]);
    }

    return networks;
  }
  
  onMount(() => {
    balances.subscribe((value) => {
      if (value) {
        // Filter out USDTE and USDTT from display list
        storedBalances = value.filter((balance) => 
          balance.isCrypto && 
          !['USDTE', 'USDTT'].includes(balance.shortName)
        );

        // Find USDT and inject networks from USDTE/USDTT
        const usdtBalance = storedBalances.find(b => b.name === "USDT");
        if (usdtBalance) {
          usdtBalance.cryptoNetworks = getUSDTNetworks(value);
        }

        selectedBalance = usdtBalance || storedBalances[0];
        
        if (selectedBalance?.cryptoNetworks.length > 0) {
          if (selectedBalance.name === "USDT") {
            selectedNetwork = selectedBalance.cryptoNetworks.find(n => n.name === "ETH") 
              || selectedBalance.cryptoNetworks[0];
          } else {
            selectedNetwork = selectedBalance.cryptoNetworks[0];
          }
        }
        
        setTimeout(() => {
          loading = false;
        }, 1000);
      }
    });
  });

  function selectBalance(balance: IBalance) {
    loading = true;
    selectedBalance = balance;
    if (balance.cryptoNetworks.length > 0) {
      if (balance.name === "USDT") {
        selectedNetwork = balance.cryptoNetworks.find(n => n.name === "ETH") 
          || balance.cryptoNetworks[0];
      } else {
        selectedNetwork = balance.cryptoNetworks[0];
      }
    }
    setTimeout(() => {
      loading = false;
    }, 1000);
  }

  function selectNetwork(network: INetwork) {
    loading = true;
    selectedNetwork = network;
    setTimeout(() => {
      loading = false;
    }, 1000);
  }

  function copy(address: string) {
    navigator.clipboard.writeText(address).then(
      () => {
        toasts.send('Address copied to clipboard', 'success');
      },
      (err) => {
        console.error('Failed to copy address to clipboard', err);
        toasts.send('Failed to copy address to clipboard', 'error');
      }
    )
  }

  // Helper function to get network display name
  function getNetworkDisplayName(network: INetwork): string {
    if (selectedBalance?.name === "USDT") {
      return network.name === "ETH" ? "ERC20" : "TRC20";
    }
    return network.name;
  }

  // Computed property for network display
  let networkDisplay = $derived(selectedNetwork ? getNetworkDisplayName(selectedNetwork) : '');
</script>

<!-- Template remains exactly the same -->
<div class="text-xs w-full font-medium my-3 md:my-5">
  <p>You can only use your crypto assets!</p>
  <p class="text-primary-fire">Choose correct cryptocurrency network!</p>
</div>

{#if selectedBalance && selectedNetwork && storedBalances}
  <div class="flex flex-col justify-center items-center">
    <div class="flex gap-3 mb-4">
      <CustomDropdown 
        selected={selectedBalance} 
        selectedTitle="name" 
        items={storedBalances} 
        hasIcon={true}
        on:selected={(item) => selectBalance(item.detail)} 
        id="cryptoCurrencyDropdown" 
      />
      {#if selectedBalance.cryptoNetworks.length > 0}
        <CustomDropdown 
          selected={{...selectedNetwork, name: networkDisplay}}
          selectedTitle="name" 
          items={selectedBalance.cryptoNetworks.map(network => ({
            ...network,
            name: selectedBalance.name === "USDT" 
              ? network.name === "ETH" ? "ERC20" : "TRC20"
              : network.name
          }))}
          hasIcon={false}
          on:selected={(item) => selectNetwork(item.detail)} 
          id="CryptoNetworkDropdown" 
        />
      {/if}
    </div>

    <div class="my-3">
      <div style="background-image: url('/QRFrame.svg'); background-size: cover; padding: 16px">
        {#if loading}
          <div class="text-center align-middle text-white h-[130px] w-[130px]">Loading...</div>
        {:else}
          <QRCode data="{selectedNetwork.address}" width={130} height={130}/>
        {/if}
      </div>
    </div>

    <div class="px-3 py-2 my-2 bg-primary-dark rounded-full text-sm flex items-center">
      {selectedNetwork.address}
      <button onclick={() => copy(selectedNetwork.address)}>
        <CopyIcon css="ml-1.5 w-5"/>
      </button>
    </div>
    {#if selectedBalance.shortName === 'XRP'}
      <div class="px-3 py-2 bg-primary-dark rounded-full text-sm flex items-center">
        <span class="mr-1">Tag:</span>
        {selectedNetwork.tag}
        <button onclick={() => copy(selectedNetwork.tag)}>
          <CopyIcon css="ml-1.5 w-5"/>
        </button>
      </div>
    {/if}

    <div class="md:w-10/12 my-2">
      <span class="text-xs">
        Only send {selectedBalance.shortName} to this address on 
        {selectedBalance.name === "USDT" ? networkDisplay : selectedNetwork.title} chain
      </span>
      <p class="text-xs mt-2">
        Scan or paste the above wallet address in your crypto wallet to make a deposit. 
        You will receive the specified {selectedBalance.shortName} amount in your Forza.bet balance.
      </p>
    </div>

    <div class="w-10/12">
      <BonusOffers type="CRYPTO" />
    </div>
  </div>
{/if}